<script setup lang="ts">
	import { useIntersectionObserver } from '@vueuse/core'
	import StringMethods from '~/utils/character'

	export type PlanInformationContext = {
		premium: StatsSchema
		list?: {
			key: string
			[id: string]: StatsSchema | string | number
		}
		upcomingDetail: StatsSchema
	}
	const { path } = useRoute()
	const { toCapitalize, replacePlaceholder } = StringMethods()
	const isBoxVisible = ref(false)
	const last_box = ref<HTMLElement | null>(null)
	const scrollEvent = ref()

	const props = defineProps<{
		value: PlanInformationContext
		filterBy?: string
	}>()
	defineEmits<{
		(event: 'change', arg: string): void
	}>()
	const sortedData = computed(() => {
		return Object.values(props.value).sort((a, b) => (a?.order > b?.order ? 1 : -1))
	})
	useIntersectionObserver(last_box, ([{ isIntersecting }]) => {
		isBoxVisible.value = isIntersecting
	})

	onMounted(() => {
		if (last_box.value) {
			scrollEvent.value = last_box.value.addEventListener('scroll', () => {
				isBoxVisible.value =
					Number(last_box.value?.offsetWidth) + Number(last_box.value?.scrollLeft) !==
					Number(last_box.value?.scrollWidth)
			})
		}
	})
	onUnmounted(() => {
		removeEventListener('scroll', scrollEvent.value)
	})
</script>

<template>
	<div
		ref="last_box"
		class="custom-scrollbar my-[32px] flex w-full flex-1 items-stretch gap-x-3 overflow-x-auto pb-4"
	>
		<div v-if="isBoxVisible" class="animate-x w-50 absolute right-0 top-14 z-50 h-full w-fit xl:hidden">
			<lazy-tools-icon dir="arrows" icon="double-greater-than" :height="40" :width="40" />
		</div>
		<template v-for="(item, k) in sortedData" :key="k">
			<dashboard-stat-card v-if="item?.key !== 'list'" class="h-fit flex-none" :value="item as StatsSchema">
				<template #premium-p>
					<shared-click-to-copy v-if="item?.planId" success-label="Plan Id" :copy-text="item?.planId || ''">
						<span class="text-sm font-medium tracking-wide text-blue-950">
							Plan ID: {{ toCapitalize(item?.planId?.slice(0, 8)) }}
						</span>
					</shared-click-to-copy>
				</template>

				<template #upcoming_premium="{ itemClass, dataItems }">
					<div>
						<h4 :class="itemClass.title" class="pr-3 font-medium text-gray-500">
							{{ dataItems?.title || 'N/a' }} -
							<span :class="[dataItems?.expired ? 'text-error-500' : 'text-primary-2']">
								{{ dataItems?.expiryDate }}
							</span>
						</h4>

						<div :class="itemClass.total">
							{{ dataItems?.premium_paid }}
						</div>

						<nuxt-link v-if="dataItems?.showBtn" :to="`${path}/renew`" class="flex items-center">
							<div class="text-sm font-medium leading-tight text-primary-2">
								Renew {{ dataItems?.expired ? 'Now' : 'Ahead' }}
							</div>
							<lazy-tools-icon
								icon="arrow-right"
								path-class="stroke-primary-2 "
								dir="arrows"
								svg-class="animate-x"
								class="ml-2.5"
							/>
						</nuxt-link>
					</div>
				</template>
			</dashboard-stat-card>

			<dashboard-stat-card
				v-else
				v-slot="{ containerClass }"
				class="relative w-full min-w-[510px] flex-1 overflow-x-auto rounded-xl border bg-white xl:min-w-0 xl:border-none"
				:value="item"
				reset-class
			>
				<template v-for="(record, index) in item" :key="index">
					<div
						v-if="typeof record === 'object'"
						:class="[containerClass, 'relative mx-3 w-full pt-2 sm:mx-0']"
					>
						<forms-filter
							v-if="record?.filter"
							:filter-by="filterBy || ''"
							:filter-collection="record?.filterList"
							:use-default-class="false"
							class="absolute -top-[20px] left-0 z-10 xl:left-0"
							@change="e => $emit('change', e)"
						>
							<template #body>
								<div
									class="inline-flex cursor-pointer items-center justify-around rounded-xl bg-primary-2 px-1.5 py-0.5 text-xs font-medium capitalize tracking-wide text-white"
								>
									<span>{{ record.filterValue }}</span>
									<lazy-tools-icon dir="arrows" icon="caret-down-r" path-class="stroke-white" />
								</div>
							</template>
						</forms-filter>
						<a-tooltip color="white" :placement="record?.filter ? 'bottom' : 'top'">
							<h4
								class="whitespace-pre pb-6 pr-3 text-sm font-medium tracking-wide text-gray-500"
								:class="record?.titleClass"
							>
								{{ record.title }}
							</h4>
							<div :class="[record?.containerClass]">
								<div
									class="pb-4 text-3xl font-medium text-blue-950 transition-all ease-in-out group-hover:text-primary-2 lg:text-[32px] xl:text-[40px]"
								>
									{{ record.total }}
								</div>
								<slot :name="`${record.key}-p`" />
							</div>
							<template v-if="record.toolTipMessage" #title>
								<span
									class="font-medium text-gray-600"
									v-html="replacePlaceholder(record.toolTipMessage, record.title)"
								/>
							</template>
						</a-tooltip>
					</div>
				</template>
			</dashboard-stat-card>
		</template>
	</div>
</template>

<style scoped>
	.animate-x {
		animation: moveXidentifier 2s linear infinite;
		transition: 1s linear;
		transform-origin: center;
		transform-style: preserve-3d;
	}
	@keyframes moveXidentifier {
		from {
			right: -5px;
		}
		to {
			transform: 0;
		}
	}
</style>
